import { Injectable } from '@angular/core';
import { DownloadPrintBaseService } from '../download-print-base.service';
import { DatePipe } from '@angular/common';

import { stringifyCSV } from '../helpers/download-inventory-helpers';
import { downloadableConversionProduct } from '@shared/models/product-conversion.model';
import { desiredColumn } from '@shared/models/desired-column';
import { DownloadRequestFileType } from '@usf/list-types';
import { SeperatedColumn } from '@shared/models/seperated-columns';
import {
  documentFunctionMap,
  nutritonalDocumentMap,
} from '../helpers/download-product-helper';
import {
  productConversionColumns,
  nonUsfProductColunms,
} from '../helpers/document-helper-models-and-enums';
import {
  createProductComparisonPdf,
  getPDFColumns,
} from '../helpers/product-pdf-helpers';
import { ServiceHandlerService } from '@shared/services/service-handler.service';
import jsPDF from 'jspdf';
import { firstValueFrom } from 'rxjs';
import { CustomerStoreService } from '@app/ngrx-customer/services';
import { createProductRows } from '../helpers/general-pdf-helper';
import { NonUSFProductViewModel } from '@inventory/pages/manage-non-usf-products/models/non-usf-products-view-model';
import { nonUsfProductsDocumentFunctionMap } from '../helpers/download-non-usf-product-helpers';

@Injectable({
  providedIn: 'root',
})
export class DownloadProductsService {
  constructor(
    private downloadPrintBaseService: DownloadPrintBaseService,
    public datePipe: DatePipe,
    private serviceHandler: ServiceHandlerService,
    private customerStoreService: CustomerStoreService,
  ) {}

  public downloadProducts = async (
    fileType: DownloadRequestFileType,
    fileName: string,
    donwloadProductsRow: downloadableConversionProduct[],
    isDownload: boolean,
    isBetterBuy: boolean,
    reasons: string[],
    dataPairs: string[][],
  ) => {
    try {
      switch (fileType) {
        case 'CSV':
          await this.generateAndSaveCSV(
            fileName,
            donwloadProductsRow,
            isBetterBuy,
          );
          break;
        case 'PDF':
          await this.generateAndSavePDF(
            fileName,
            donwloadProductsRow,
            isBetterBuy,
            reasons,
            dataPairs,
          );
          break;
        default:
          this.downloadPrintBaseService.cleanUp();
          break;
      }
    } catch (error) {
      this.downloadPrintBaseService.cleanUp();
      // Update error toast
      this.downloadPrintBaseService.showErrorToast(
        isDownload ? 'i18n.lists.downloadError' : 'i18n.lists.printListError',
      );
      console.error('Error creating file', error);
    }
  };

  generateAndSaveCSV = async (
    fileName: string,
    listRows: downloadableConversionProduct[],
    isBetterBuy: boolean,
  ) => {
    const csv = this.generateCSV(listRows, isBetterBuy);
    await this.downloadPrintBaseService.saveDocument(
      csv,
      fileName,
      'CSV',
      true,
    );
  };

  private generateCSV(
    productRows: downloadableConversionProduct[],
    isBetterBuy: boolean,
  ) {
    const columnsConfig = this.getConversionCSVConfig(isBetterBuy);
    const seperatedColumns =
      this.createMultipleSeperatedColumnsForConversionProducts(
        productRows,
        columnsConfig,
        'CSV',
      );
    return stringifyCSV(seperatedColumns);
  }

  async generatePDF(
    productRows: downloadableConversionProduct[],
    isBetterBuy: boolean,
    reasons: string[],
    dataPairs: string[][],
    fileName: string,
  ) {
    const columnsConfig = getPDFColumns();
    const seperatedColumns =
      this.createMultipleSeperatedColumnsForConversionProducts(
        productRows,
        columnsConfig,
        'PDF',
      );
    const doc = new jsPDF('l', 'px', [1440, 1055]);
    doc.setProperties({ title: fileName });

    const customerDivisionDepartment = await firstValueFrom(
      this.customerStoreService.selectCustomerDivisionDepartment$(),
    );
    const productData = createProductRows(seperatedColumns, reasons, false);
    const reasonForDoc = reasons.filter((reason, index) => {
      return index % 2 === 0;
    });
    const toReturn = await createProductComparisonPdf(
      doc,
      isBetterBuy,
      customerDivisionDepartment,
      columnsConfig,
      false,
      productRows.length / 2,
      productData.imageKeys,
      this.serviceHandler,
      productData.productRowsByGroup,
      reasonForDoc,
      dataPairs,
    );
    return toReturn.output('blob');
  }

  generateAndSavePDF = async (
    fileName: string,
    listRows: downloadableConversionProduct[],
    isBetterBuy: boolean,
    reasons: string[],
    dataPairs: string[][],
  ) => {
    const pdf = await this.generatePDF(
      listRows,
      isBetterBuy,
      reasons,
      dataPairs,
      fileName,
    );
    await this.downloadPrintBaseService.saveDocument(
      pdf,
      fileName,
      'PDF',
      false,
    );
  };

  createMultipleSeperatedColumnsForConversionProducts(
    products: downloadableConversionProduct[],
    columnConfig: desiredColumn[],
    fileType: DownloadRequestFileType,
  ): SeperatedColumn[] {
    let columnMap = new Map<string, SeperatedColumn>();
    const isPdf = fileType === 'PDF';
    products.forEach((productRow: downloadableConversionProduct) => {
      columnConfig.forEach(column => {
        if (column.columnType > 27) {
          columnMap = nutritonalDocumentMap[column.columnType](
            column.columnName,
            columnMap,
            productRow,
            fileType,
          );
        } else {
          columnMap = documentFunctionMap[column.columnType](
            column.columnName,
            columnMap,
            productRow,
            fileType,
          );
        }
      });

      // add an empty line of data
      if (!productRow.isOriginal && !isPdf) {
        columnMap.forEach(col => {
          col.columnValues?.push('""');
        });
      }
    });

    let seperatedColumns: SeperatedColumn[] = [];
    columnMap.forEach(column => {
      seperatedColumns.push(column);
    });
    return seperatedColumns;
  }

  // Configs:
  getConversionCSVConfig = (isBetterBuy: boolean): desiredColumn[] => {
    const seperatedColumns = [
      {
        columnName: '',
        columnType: productConversionColumns.isOriginal,
      },
      {
        columnName: 'PRODUCT NUMBER',
        columnType: productConversionColumns.productNumber,
      },
      {
        columnName: 'PRODUCT DESCRIPTION',
        columnType: productConversionColumns.productDescription,
      },
      {
        columnName: 'BRAND NAME',
        columnType: productConversionColumns.productBrand,
      },
      {
        columnName: 'PACK SIZE',
        columnType: productConversionColumns.productPackageSize,
      },
      {
        columnName: 'CS / LB PRICE',
        columnType: productConversionColumns.casePrice,
      },
      {
        columnName: 'UOM',
        columnType: productConversionColumns.caseUOM,
      },
      {
        columnName: 'EA PRICE',
        columnType: productConversionColumns.eachPrice,
      },
      // can't have two of the same column name
      {
        columnName: 'UOM ',
        columnType: productConversionColumns.eachUOM,
      },
      {
        columnName: 'PORTION PRICE',
        columnType: productConversionColumns.portionPrice,
      },
      {
        columnName: 'PRODUCT STATUS CODE',
        columnType: productConversionColumns.statusCode,
      },
      {
        columnName: 'PRODUCT STATUS DESCRIPTION',
        columnType: productConversionColumns.productStatus,
      },
    ];

    if (!isBetterBuy) {
      seperatedColumns.push({
        columnName: 'UPDATED LISTS',
        columnType: productConversionColumns.updatedLists,
      });
    }

    seperatedColumns.push(
      {
        columnName: 'MLM PRODUCT',
        columnType: productConversionColumns.mlmProduct,
      },
      {
        columnName: 'STORAGE CODE',
        columnType: productConversionColumns.storageCode,
      },
      {
        columnName: 'USF STORAGE',
        columnType: productConversionColumns.usfStorage,
      },
      {
        columnName: 'NET WEIGHT (lb)',
        columnType: productConversionColumns.netWeight,
      },
      {
        columnName: 'CONTRACT ICON',
        columnType: productConversionColumns.contractIcon,
      },
      {
        columnName: 'LOCAL',
        columnType: productConversionColumns.local,
      },
      {
        columnName: 'JIT',
        columnType: productConversionColumns.jit,
      },
      {
        columnName: 'DIRECT',
        columnType: productConversionColumns.direct,
      },
      // {
      //   columnName: 'CUSTOMER MANAGED PILL',
      //   columnType: productConversionColumns.customerPill,
      // },
      {
        columnName: 'PRODUCT CLASS #',
        columnType: productConversionColumns.classNum,
      },
      {
        columnName: 'PRODUCT CLASS',
        columnType: productConversionColumns.classDesc,
      },
      {
        columnName: 'PRODUCT CATEGORY #',
        columnType: productConversionColumns.categoryNum,
      },
      {
        columnName: 'PRODUCT CATEGORY',
        columnType: productConversionColumns.categoryDesc,
      },
      {
        columnName: 'PRODUCT GROUP #',
        columnType: productConversionColumns.groupNum,
      },
      {
        columnName: 'PRODUCT GROUP',
        columnType: productConversionColumns.groupDesc,
      },
      {
        columnName: 'SERVING SIZE',
        columnType: productConversionColumns.servingSize,
      },
      {
        columnName: 'SERVING SIZE UOM',
        columnType: productConversionColumns.ServingUOM,
      },
      {
        columnName: 'SERVINGS PER TRADE ITEM',
        columnType: productConversionColumns.servingPerTrade,
      },
      {
        columnName: 'CALORIES',
        columnType: productConversionColumns.calories,
      },
      {
        columnName: 'CALORIES FROM FAT (g)',
        columnType: productConversionColumns.caloriesFat,
      },
      {
        columnName: 'TOTAL FAT (g)',
        columnType: productConversionColumns.totalFat,
      },
      {
        columnName: 'SATURATED FAT (g)',
        columnType: productConversionColumns.satFat,
      },
      {
        columnName: 'TRANS FATTY ACIDS (g)',
        columnType: productConversionColumns.transFat,
      },
      {
        columnName: 'CHOLESTEROL (mg)',
        columnType: productConversionColumns.cholest,
      },
      {
        columnName: 'SODIUM (mg)',
        columnType: productConversionColumns.sodium,
      },
      {
        columnName: 'TOTAL CARBOHYDRATES (g)',
        columnType: productConversionColumns.totalCarb,
      },
      {
        columnName: 'DIETARY FIBER (g)',
        columnType: productConversionColumns.dietFiber,
      },
      {
        columnName: 'TOTAL SUGAR (g)',
        columnType: productConversionColumns.totalSug,
      },
      {
        columnName: 'PROTEIN (g)',
        columnType: productConversionColumns.protein,
      },
      // {
      //   columnName: 'ALLERGENS - CRUSTACEAN',
      //   columnType: productConversionColumns.alCrust,
      // },
      // {
      //   columnName: 'ALLERGENS - EGGS',
      //   columnType: productConversionColumns.alEgg,
      // },
      // {
      //   columnName: 'ALLERGENS - FISH',
      //   columnType: productConversionColumns.alFish,
      // },
      // {
      //   columnName: 'ALLERGENS - MILK',
      //   columnType: productConversionColumns.alMilk,
      // }, {
      //   columnName: 'ALLERGENS - PEANUTS',
      //   columnType: productConversionColumns.alPeanuts,
      // },
      // {
      //   columnName: 'ALLERGENS - SOY',
      //   columnType: productConversionColumns.alSoy,
      // },
      // {
      //   columnName: 'ALLERGENS - TREE NUTS',
      //   columnType: productConversionColumns.alTreeNut,
      // },
      // {
      //   columnName: 'ALLERGENS - WHEAT',
      //   columnType: productConversionColumns.alWheat,
      // }, {
      //   columnName: 'ALLERGENS - MUSTARD',
      //   columnType: productConversionColumns.alMust,
      // },
      {
        columnName: 'CHILD NUTRITION CERTIFICATION',
        columnType: productConversionColumns.childNutrition,
      },
      {
        columnName: 'CHOLESTEROL FREE',
        columnType: productConversionColumns.cholestFree,
      },
      {
        columnName: 'FAT FREE',
        columnType: productConversionColumns.fatFree,
      },
      {
        columnName: 'LOW FAT',
        columnType: productConversionColumns.lowFat,
      },
      {
        columnName: 'NO SUGAR ADDED',
        columnType: productConversionColumns.noSug,
      },
      {
        columnName: 'LOCALLY SOURCED',
        columnType: productConversionColumns.localSource,
      },
      {
        columnName: 'KOSHER',
        columnType: productConversionColumns.kosher,
      },
      {
        columnName: 'HALAL',
        columnType: productConversionColumns.halal,
      },
      {
        columnName: 'CERTIFIED ANGUS BEEF',
        columnType: productConversionColumns.certBeef,
      },
      {
        columnName: 'ORGANIC',
        columnType: productConversionColumns.organic,
      },
      // {
      //   columnName: 'NO ADDED SYNTHETIC HORMONES',
      //   columnType: productConversionColumns.noAddedHorm,
      // },
      {
        columnName: 'ADDITIONAL INFO',
        columnType: productConversionColumns.addInfo,
      },
      {
        columnName: 'INGREDIENTS STATEMENT',
        columnType: productConversionColumns.ingStatement,
      },
    );
    return seperatedColumns;
  };

  public downloadNonUsfProducts = async (
    nonUsfProducts: NonUSFProductViewModel[],
  ) => {
    const defaultFileName = 'Non-US Foods Products';

    try {
      await this.generateAndSaveNonUSFProductDataCSV(
        defaultFileName,
        nonUsfProducts,
      );
    } catch (error) {
      this.downloadPrintBaseService.cleanUp();
      // Update error toast
      this.downloadPrintBaseService.showErrorToast('i18n.lists.downloadError');
      console.error('Error creating file', error);
    }
  };

  generateAndSaveNonUSFProductDataCSV = async (
    fileName: string,
    nonUsfProducts: NonUSFProductViewModel[],
  ) => {
    const csv = this.generateCSVForNonUsfProducts(nonUsfProducts);
    await this.downloadPrintBaseService.saveDocument(
      csv,
      fileName,
      'CSV',
      true,
    );
  };

  private generateCSVForNonUsfProducts(
    nonUsfProducts: NonUSFProductViewModel[],
  ) {
    const columnsConfig = this.getNonUsfProductsCSVConfig();
    const seperatedColumns =
      this.createMultipleSeperatedColumnsForNonUsfProducts(
        nonUsfProducts,
        columnsConfig,
        'CSV',
      );
    return stringifyCSV(seperatedColumns);
  }

  createMultipleSeperatedColumnsForNonUsfProducts(
    nonUsfProducts: NonUSFProductViewModel[],
    columnConfig: desiredColumn[],
    fileType: DownloadRequestFileType,
  ): SeperatedColumn[] {
    let columnMap = new Map<string, SeperatedColumn>();

    nonUsfProducts.forEach((nonUsfProduct: NonUSFProductViewModel) => {
      columnConfig.forEach(column => {
        columnMap = nonUsfProductsDocumentFunctionMap[column.columnType](
          column.columnName,
          columnMap,
          nonUsfProduct,
          fileType,
        );
      });
    });

    let seperatedColumns: SeperatedColumn[] = [];
    columnMap.forEach(column => {
      seperatedColumns.push(column);
    });
    return seperatedColumns;
  }

  getNonUsfProductsCSVConfig = (): desiredColumn[] => {
    const seperatedColumns = [
      {
        columnName: 'Product Name / Description',
        columnType: nonUsfProductColunms.productDescription,
      },
      {
        columnName: 'Product Number',
        columnType: nonUsfProductColunms.productNumber,
      },
      {
        columnName: 'Vendor',
        columnType: nonUsfProductColunms.vendor,
      },
      {
        columnName: 'Brand',
        columnType: nonUsfProductColunms.brand,
      },
      {
        columnName: 'Manufacturer #',
        columnType: nonUsfProductColunms.manufacturerNumber,
      },
      {
        columnName: 'Pack Size',
        columnType: nonUsfProductColunms.packSize,
      },
      {
        columnName: 'Avg Net Weight (lbs)',
        columnType: nonUsfProductColunms.avgNetWeight,
      },
      {
        columnName: 'Price ($)',
        columnType: nonUsfProductColunms.price,
      },
      {
        columnName: 'Price UOM',
        columnType: nonUsfProductColunms.priceUom,
      },
      {
        columnName: 'Unit Description',
        columnType: nonUsfProductColunms.unitDescription,
      },
      {
        columnName: 'Units Per Case',
        columnType: nonUsfProductColunms.unitsPerCase,
      },
      {
        columnName: 'GL Code',
        columnType: nonUsfProductColunms.glCode,
      },
    ];

    return seperatedColumns;
  };
}
